import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ShopBottomNav from '../../components/ShopBottomNav';
import {
  getTokenBalance,
  getSubscription,
  updateDeal,
  STATUS,
  refreshTokenBalance,
} from '../../services/shopState';
import { getMyDeals, updateDealStatus } from '../../services/apiService';
import './ShopDashboardPage.css';

const isDemoMode = () => sessionStorage.getItem('walkin_demo_mode') === 'true';

const REVENUE_PER_REDEMPTION = 23;

function formatRelativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return 'Zojuist';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min geleden`;
  // Same day → show time
  const sameDay = new Date(iso).toDateString() === new Date().toDateString();
  if (sameDay) {
    return new Date(iso).toLocaleTimeString('nl-NL', {
      hour: '2-digit', minute: '2-digit',
    });
  }
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'short',
  });
}

function formatTodayBadge(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });
}

// The localStorage store, kept as the demo path and as the fallback for when
// the API cannot be reached. In live mode the backend is the source of truth:
// deals created on another device never reach this store, so reading it alone
// showed a dashboard that disagreed with the deals page.
function readMyDeals(shopName) {
  let all = [];
  try {
    all = JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]');
  } catch { all = []; }
  if (!shopName) return [];
  return all.filter(
    (d) => d.shopName === shopName || d.createdBy === shopName
  );
}

// Still local-only, and not for want of trying: the backend has no endpoint
// that lists a retailer's redemptions. getRecentRedemptions() in apiService
// points at GET /redemptions/recent, which 404s — the redemptions controller
// only exposes generate-qr, qr-status, scan and scan-code. Wiring this up
// needs a backend route first.
function readRedemptions(shopName) {
  try {
    return JSON.parse(localStorage.getItem(`walkin_redemptions_${shopName}`) || '[]');
  } catch {
    return [];
  }
}

async function loadMyDeals(shopName) {
  if (isDemoMode()) return readMyDeals(shopName);
  try { return await getMyDeals(); }
  catch { return readMyDeals(shopName); }
}

function readMyShopFromStorage() {
  const email = sessionStorage.getItem('walkin_shop_email');
  if (!email) return null;
  let registered = [];
  let adminCreated = [];
  try { registered = JSON.parse(localStorage.getItem('walkin_shops') || '[]'); } catch { registered = []; }
  try { adminCreated = JSON.parse(localStorage.getItem('walkin_admin_shops') || '[]'); } catch { adminCreated = []; }
  return [...registered, ...adminCreated].find(
    (s) => (s.email || '').toLowerCase() === email.toLowerCase()
  ) || null;
}

// Refresh session keys from the canonical localStorage shop record. Called
// on every dashboard mount so a shop owner always sees the latest area/name
// after an admin edit, even if their session is stale.
function refreshSessionFromStorage() {
  const email = sessionStorage.getItem('walkin_shop_email');
  if (!email) return;
  let registered = [];
  let adminCreated = [];
  try { registered = JSON.parse(localStorage.getItem('walkin_shops') || '[]'); } catch { registered = []; }
  try { adminCreated = JSON.parse(localStorage.getItem('walkin_admin_shops') || '[]'); } catch { adminCreated = []; }
  const all = [...registered, ...adminCreated];
  const shop = all.find((s) => (s.email || '').toLowerCase() === email.toLowerCase());
  if (!shop) return;
  sessionStorage.setItem('walkin_shop_area', shop.area || '');
  sessionStorage.setItem('walkin_shop_name', shop.shopName || '');
  sessionStorage.setItem('walkin_shop_subscription', shop.subscription || 'tokens');
  // Token counters are deliberately NOT copied from this browser record any
  // more: the wallet lives in the backend (refreshTokenBalance), and this copy
  // overwrote it with a stale number on every render — which is why a balance
  // changed by an admin never showed up here.
}

export default function ShopDashboardPage() {
  const navigate = useNavigate();
  refreshSessionFromStorage();
  const shopName = sessionStorage.getItem('walkin_shop_name') || 'Mijn winkel';

  const [tokens, setTokens] = useState(getTokenBalance());
  const [subscription, setSubscription] = useState(getSubscription());
  const [myDeals, setMyDeals] = useState(() => readMyDeals(shopName));
  const [redemptions, setRedemptions] = useState(() => readRedemptions(shopName));
  const [myShop, setMyShop] = useState(() => readMyShopFromStorage());

  const refresh = useCallback(async () => {
    refreshSessionFromStorage();
    // Pull the wallet from the backend on every refresh, not just on mount.
    // It resolves after this function returns and only fires
    // walkin-token-change when a value actually moved — which runs refresh
    // once more to render it. The second pass reads the same values, fires
    // nothing, and it settles. Without this the focus/visibility/30s
    // handlers below all re-render the stale sessionStorage number.
    refreshTokenBalance();
    setTokens(getTokenBalance());
    setSubscription(getSubscription());
    setRedemptions(readRedemptions(shopName));
    setMyShop(readMyShopFromStorage());
    // Awaited last so the synchronous reads above paint immediately rather
    // than waiting on the network.
    setMyDeals(await loadMyDeals(shopName));
  }, [shopName]);

  useEffect(() => {
    refresh();
    const onStorage = (e) => {
      if (!e || !e.key ||
          e.key === 'walkin_shop_deals' ||
          e.key === 'walkin_shops' ||
          e.key === 'walkin_admin_shops' ||
          e.key === `walkin_redemptions_${shopName}`) refresh();
    };
    window.addEventListener('walkin-token-change', refresh);
    window.addEventListener('walkin-deals-change', refresh);
    window.addEventListener('walkin-admin-shops-change', refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', refresh);
    const intervalId = setInterval(refresh, 30000);
    return () => {
      window.removeEventListener('walkin-token-change', refresh);
      window.removeEventListener('walkin-deals-change', refresh);
      window.removeEventListener('walkin-admin-shops-change', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', refresh);
      clearInterval(intervalId);
    };
  }, [refresh, shopName]);

  const activeDeals = myDeals.filter((d) => d.status === STATUS.ACTIVE);
  const totalViews = myDeals.reduce((sum, d) => sum + (d.views || 0), 0);
  const totalRedemptions = myDeals.reduce((sum, d) => sum + (d.redemptions || 0), 0);
  const totalShares = myDeals.reduce((sum, d) => sum + (d.shares || 0), 0);
  const conversion = totalViews > 0
    ? Math.round((totalRedemptions / totalViews) * 100)
    : 0;
  const estimatedRevenue = totalRedemptions * REVENUE_PER_REDEMPTION;

  const handleLogout = () => {
    sessionStorage.removeItem('walkin_shop_mode');
    sessionStorage.removeItem('walkin_shop_name');
    sessionStorage.removeItem('walkin_shop_area');
    sessionStorage.removeItem('walkin_shop_email');
    navigate('/');
  };

  const handlePauseToggle = async (deal) => {
    if (deal.status === STATUS.EXPIRED || deal.status === STATUS.DELETED) return;
    const next = deal.status === STATUS.ACTIVE ? STATUS.PAUSED : STATUS.ACTIVE;

    if (isDemoMode()) {
      updateDeal(deal.id, { status: next });
      return;
    }

    try {
      await updateDealStatus(deal.id, next);
    } catch {
      // This page has no toast. Rather than leave the toggle showing a state
      // the backend never accepted, fall through to the refresh below, which
      // re-reads the deals and snaps it back to the truth.
    }
    await refresh();
  };

  return (
    <div className="shop-dash">
      <header className="shop-dash__header">
        <div className="shop-dash__header-row">
          <div className="shop-dash__brand">
            <div className="shop-dash__logo">
              <span className="shop-dash__logo-walk">Walk</span>
              <span className="shop-dash__logo-in">In</span>
            </div>
            <span className="shop-dash__badge">Winkeliers modus</span>
          </div>
          <button
            className="shop-dash__logout"
            onClick={handleLogout}
            aria-label="Uitloggen"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
        <h1 className="shop-dash__shopname">{shopName}</h1>
      </header>

      <main className="shop-dash__main">
        {myShop?.status === 'inactief' && (
          <div className="shop-dash__status-banner shop-dash__status-banner--warning">
            <div className="shop-dash__status-title">⏸ Je account is inactief</div>
            <p>
              Je deals zijn momenteel niet zichtbaar voor klanten. Neem contact
              op met WalkIn voor meer informatie.
            </p>
            <a href="mailto:hello@walkin.nl">hello@walkin.nl</a>
          </div>
        )}
        {myShop?.status === 'geblokkeerd' && (
          <div className="shop-dash__status-banner shop-dash__status-banner--danger">
            <div className="shop-dash__status-title">🚫 Je account is geblokkeerd</div>
            <p>
              Je hebt geen toegang tot het platform. Neem contact op met WalkIn.
            </p>
            <a href="mailto:hello@walkin.nl">hello@walkin.nl</a>
          </div>
        )}
        {/* Stats — real numbers from localStorage */}
        <section className="shop-dash__section">
          <h2 className="shop-dash__section-title">Vandaag</h2>
          <div className="shop-dash__stats">
            <div className="shop-dash__stat">
              <span className="shop-dash__stat-label">Deals bekeken</span>
              <span className="shop-dash__stat-value">{totalViews.toLocaleString('nl-NL')}</span>
            </div>
            <div className="shop-dash__stat">
              <span className="shop-dash__stat-label">Deals ingeleverd</span>
              <span className="shop-dash__stat-value">{totalRedemptions}</span>
            </div>
            <div className="shop-dash__stat">
              <span className="shop-dash__stat-label">Conversie</span>
              <span className="shop-dash__stat-value">{conversion}%</span>
            </div>
            <div className="shop-dash__stat">
              <span className="shop-dash__stat-label">Geschatte omzet</span>
              <span className="shop-dash__stat-value">€{estimatedRevenue.toLocaleString('nl-NL')}</span>
            </div>
            <div className="shop-dash__stat">
              <span className="shop-dash__stat-label">📤 Gedeeld</span>
              <span className="shop-dash__stat-value">{totalShares} keer</span>
            </div>
          </div>
        </section>

        {/* Token / subscription card (unchanged) */}
        {subscription === 'unlimited' || tokens.unlimited ? (
          <section
            className="shop-dash__sub shop-dash__sub--unlimited"
            onClick={() => navigate('/shop/tokens')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate('/shop/tokens');
            }}
          >
            <div className="shop-dash__sub-head">
              <span className="shop-dash__sub-check">✅</span>
              <div>
                <div className="shop-dash__sub-title">Unlimited abonnement actief</div>
                <div className="shop-dash__sub-sub">€99,00 ex BTW / maand</div>
              </div>
            </div>
            <div className="shop-dash__sub-feature">Onbeperkt deals aanmaken</div>
            <div className="shop-dash__sub-cta">Beheer abonnement →</div>
          </section>
        ) : (
          <section
            className={
              'shop-dash__tokens ' +
              (tokens.available === 0
                ? 'shop-dash__tokens--urgent'
                : tokens.available <= 2
                  ? 'shop-dash__tokens--warning'
                  : 'shop-dash__tokens--ok')
            }
            onClick={() => navigate('/shop/tokens')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate('/shop/tokens');
            }}
          >
            <div className="shop-dash__tokens-head">
              <div className="shop-dash__tokens-icon">🪙</div>
              <div>
                <div className="shop-dash__tokens-title">Token saldo</div>
                <div className="shop-dash__tokens-big">
                  <strong>{tokens.available}</strong> beschikbaar
                </div>
              </div>
            </div>
            <div className="shop-dash__tokens-stats">
              <span>Gekocht: <strong>{tokens.purchased}</strong></span>
              <span>·</span>
              <span>Gebruikt: <strong>{tokens.used}</strong></span>
              <span>·</span>
              <span>Beschikbaar: <strong>{tokens.available}</strong></span>
            </div>
            <div className="shop-dash__tokens-cta">
              {tokens.available === 0 ? 'Tokens kopen — geen meer →' : 'Bekijk pakketten →'}
            </div>
          </section>
        )}

        {/* Quick actions */}
        <section className="shop-dash__actions">
          <button
            className="shop-dash__action shop-dash__action--orange"
            onClick={() => navigate('/shop/new-deal')}
          >
            <span className="shop-dash__action-icon">➕</span>
            <span>Nieuwe deal</span>
          </button>
          <button
            className="shop-dash__action shop-dash__action--blue"
            onClick={() => navigate('/shop/scan')}
          >
            <span className="shop-dash__action-icon">📷</span>
            <span>Scan QR code</span>
          </button>
        </section>

        {/* Active deals — real */}
        <section className="shop-dash__section">
          <h2 className="shop-dash__section-title">Actieve deals</h2>
          {activeDeals.length === 0 ? (
            <div className="shop-dash__no-deals">
              <span className="shop-dash__no-deals-icon">🏷️</span>
              <p>Geen actieve deals</p>
              <button
                className="shop-dash__no-deals-btn"
                onClick={() => navigate('/shop/new-deal')}
              >
                ➕ Maak je eerste deal aan
              </button>
            </div>
          ) : (
            <div className="shop-dash__deals">
              {activeDeals.map((deal) => (
                <div key={deal.id} className="shop-dash__deal">
                  <div className="shop-dash__deal-head">
                    <span className="shop-dash__deal-emoji">{deal.emoji || '🏷️'}</span>
                    <div className="shop-dash__deal-text">
                      <div className="shop-dash__deal-title">{deal.title}</div>
                      <div className="shop-dash__deal-status">🟢 Actief</div>
                    </div>
                  </div>
                  <div className="shop-dash__deal-meta">
                    <span>Bekeken: <strong>{deal.views || 0}</strong></span>
                    <span>Ingeleverd: <strong>{deal.redemptions || 0}</strong></span>
                    <span>📤 {deal.shares || 0} keer gedeeld</span>
                    <span>Tot {formatTodayBadge(deal.expiresAt)} 23:59</span>
                  </div>
                  <div className="shop-dash__deal-actions">
                    <button
                      className="shop-dash__deal-btn shop-dash__deal-btn--edit"
                      onClick={() => navigate(`/shop/edit-deal/${deal.id}`)}
                    >
                      ✏️ Aanpassen
                    </button>
                    <button
                      className="shop-dash__deal-btn shop-dash__deal-btn--pause"
                      onClick={() => handlePauseToggle(deal)}
                    >
                      ⏸ Pauzeer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recent redemptions — real */}
        <section className="shop-dash__section">
          <h2 className="shop-dash__section-title">Recente inleveringen</h2>
          {redemptions.length === 0 ? (
            <div className="shop-dash__no-redemptions">
              <p>Nog geen inleveringen vandaag</p>
              <p className="small">
                Deals worden hier getoond zodra klanten ze gebruiken.
              </p>
            </div>
          ) : (
            <ul className="shop-dash__redemptions">
              {redemptions.slice(0, 5).map((r) => (
                <li key={r.id} className="shop-dash__redemption">
                  <span className="shop-dash__redemption-emoji">{r.dealEmoji || '🏷️'}</span>
                  <span className="shop-dash__redemption-code">Code {r.code}</span>
                  <span className="shop-dash__redemption-time">{formatRelativeTime(r.redeemedAt || r.time)}</span>
                  <span className="shop-dash__redemption-deal">{r.dealTitle}</span>
                  <span className="shop-dash__redemption-check">✅</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      <ShopBottomNav />
    </div>
  );
}
